import { useState, useRef } from "react";
import { TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, X, ExternalLink } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ReceiptFilesCellProps {
  expenseId: string;
  receiptUrls: string[];
  onUpdate: (urls: string[]) => void;
}

export const ReceiptFilesCell = ({
  expenseId,
  receiptUrls,
  onUpdate,
}: ReceiptFilesCellProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILES = 5;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_FILES - receiptUrls.length;
    if (remaining <= 0) {
      toast.error("Maximum 5 files per expense");
      return;
    }

    const toUpload = files.slice(0, remaining);
    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const newUrls: string[] = [];
      for (const file of toUpload) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 10MB`);
          continue;
        }
        const ext = file.name.split(".").pop();
        const fileName = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage
          .from("expense-receipts")
          .upload(fileName, file);
        if (error) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
        const { data: { publicUrl } } = supabase.storage
          .from("expense-receipts")
          .getPublicUrl(fileName);
        newUrls.push(publicUrl);
      }

      if (newUrls.length > 0) {
        onUpdate([...receiptUrls, ...newUrls]);
        toast.success(`${newUrls.length} file(s) attached`);
      }
    } catch (err: any) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = (index: number) => {
    setDeleteConfirmIndex(index);
  };

  const confirmDelete = () => {
    if (deleteConfirmIndex !== null) {
      const updated = receiptUrls.filter((_, i) => i !== deleteConfirmIndex);
      onUpdate(updated);
      setDeleteConfirmIndex(null);
    }
  };

  const count = receiptUrls.length;

  return (
    <>
      <TableCell className="p-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="h-9 w-full justify-start text-left font-normal px-2 gap-1.5"
            >
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
              {count > 0 ? (
                <span className="text-xs">{count} file{count !== 1 ? "s" : ""}</span>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <div className="space-y-2">
              <p className="text-xs font-medium">Receipts ({count}/{MAX_FILES})</p>

              {receiptUrls.map((url, i) => {
                const filename = url.split("/").pop() || `File ${i + 1}`;
                return (
                  <div key={i} className="flex items-center gap-1 text-xs">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate text-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3 shrink-0" />
                      <span className="truncate">{filename.slice(0, 20)}</span>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 text-destructive hover:text-destructive"
                      onClick={() => handleRemove(i)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}

              {count < MAX_FILES && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    {isUploading ? "Uploading..." : "Add File"}
                  </Button>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </TableCell>

      <AlertDialog open={deleteConfirmIndex !== null} onOpenChange={(open) => {
        if (!open) setDeleteConfirmIndex(null);
      }}>
        <AlertDialogContent>
          <AlertDialogTitle>Delete Receipt?</AlertDialogTitle>
          <AlertDialogDescription>
            This will remove the receipt from this expense. This action cannot be undone.
          </AlertDialogDescription>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
